import {APP_INITIALIZER, InjectionToken, NgModule} from '@angular/core';
import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';

import {AuthGuard} from '@app/core/guards/auth.guard';
import {NoAuthGuard} from '@app/core/guards/noAuth.guard';
import {AdminGuard} from '@app/core/guards/admin.guard';
import {UserGuard} from '@app/core/guards/user.guard';

import {TokenInterceptor} from '@app/core/interceptors/token.interceptor';
import {AppInitializer} from '@app/core/app.initializer';
import {AuthService} from '@app/core/services/auth.service';
import {InternationalizationService} from '@app/core/services/internationalization.service';
import {DateService} from '@app/core/services/date.service';
import {IAppState} from '@app/core/store/state/app.state';
import {ActionReducerMap, Store, StoreModule} from '@ngrx/store';
import {PayrollEffects} from '@app/core/store/effects/payrolls.effects';
import {ErrorEffects} from '@app/core/store/effects/error.effects';
import {EffectsModule} from '@ngrx/effects';
import {StoreRouterConnectingModule} from '@ngrx/router-store';
import {environment} from '@env/environment';
import {StoreDevtoolsModule} from '@ngrx/store-devtools';
import {appReducers} from '@app/core/store/reducers/app.reducers';
import {PayrollService} from '@app/core/services/api/payroll.service';
import {NewSalaryExportEffects} from '@app/core/store/effects/new-salary-export.effects';
import {IntegrationService} from '@app/core/services/api/integration.service';
import {ShiftDraftService} from '@app/core/services/api/shiftDraft.service';
import {ScheduleEffects} from '@app/core/store/effects/schedule.effects';
import {ShiftDraftsEffects} from '@app/core/store/effects/shift-drafts.effects';
import {LayoutEffects} from '@app/core/store/effects/layout.effects';
import {DepartmentSettingsEffects} from '@app/core/store/effects/department-settings.effects';
import {SubscriptionEffects} from '@app/core/store/effects/subscription.effects';
import {NullToEmptyStringInterceptor} from '@app/core/interceptors/nullToEmptyString.interceptor';
import {SessionEffects} from '@app/core/store/effects/session.effects';
import {AngularWebLibModule} from '@quickorder/angular-web-lib';
import {CommonModule} from '@angular/common';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {TextMaskModule} from 'angular2-text-mask';
import {SharedModule} from '@app/modules/shared/shared.module';
import {DayNotePopupComponent} from '@app/core/popups/day-note-popup/day-note-popup.component';
import {ShiftPopupComponent} from '@app/core/popups/shift-popup/shift-popup.component';
import {DayNoteService} from '@app/core/services/api/day-note.service';
import {DayNotesEffects} from '@app/core/store/effects/daynotes.effects';
import {StatisticsEffects} from '@app/core/store/effects/statistics.effects';
import {StatisticService} from '@app/core/services/api/statistic.service';
import {SelectShiftDraftPopupComponent} from '@app/core/popups/select-shift-draft-popup/select-shift-draft-popup.component';
import {ShiftTemplatesEffects} from '@app/core/store/effects/shift-templates.effects';
import {ShiftTemplateService} from '@app/core/services/api/shift-template.service';
import {SelectShiftTemplatePopupComponent} from '@app/core/popups/select-shift-template-popup/select-shift-template-popup.component';
import {DayNotesPopupComponent} from '@app/core/popups/day-notes-popup/day-notes-popup.component';
import {EmployeesEffects} from '@app/core/store/effects/employees.effects';
import {NotificationsEffects} from '@app/core/store/effects/notifications.effects';
import {AvailabilitiesEffects} from '@app/core/store/effects/availabilities.effects';
import {BulletinBoardEffects} from '@app/core/store/effects/bulletin-board.effects';
import {FileEntryEffects} from '@app/core/store/effects/file-entry.effects';
import {ActionsEffects} from '@app/core/store/effects/actions.effects';
import {ActivityService} from '@app/core/services/api/activity.service';
import {ActivityEffects} from '@app/core/store/effects/activity.effects';
import {DepartmentEffects} from '@app/core/store/effects/department.effects';
import {MessagesEffects} from '@app/core/store/effects/messages.effects';
import {IntegrationsEffects} from '@app/core/store/effects/integrations.effects';
import {SupplementalSalaryEffects} from '@app/core/store/effects/supplementalSalary.effects';
import {DashboardEffects} from '@app/core/store/effects/dashboard.effects';
import {PunchClockEffects} from '@app/core/store/effects/punch-clock.effects';
import {LocalStorageService} from '@app/core/services/local-storage.service';
import {SickDaysEffects} from '@app/core/store/effects/sick-days.effects';

export const REDUCER_TOKEN = new InjectionToken<ActionReducerMap<IAppState>>('Registered Reducers');

export function getReducers(someService: any): any {
	return appReducers;
}

export function serializeNGRXValues(key, value): any {
	return (key === 'interService' || key === 'deps' || key === 'injector') ? '' : value;
}

@NgModule({
	imports: [
		HttpClientModule,
		StoreRouterConnectingModule.forRoot({stateKey: 'router'}),
		StoreModule.forRoot(REDUCER_TOKEN),
		EffectsModule.forRoot([
			ErrorEffects,
			PayrollEffects,
			NewSalaryExportEffects,
			ScheduleEffects,
			ShiftDraftsEffects,
			LayoutEffects,
			DepartmentSettingsEffects,
			SubscriptionEffects,
			SessionEffects,
			DayNotesEffects,
			StatisticsEffects,
			ShiftTemplatesEffects,
			EmployeesEffects,
			NotificationsEffects,
			AvailabilitiesEffects,
			ActionsEffects,
			ActivityEffects,
			DepartmentEffects,
			MessagesEffects,
			BulletinBoardEffects,
			FileEntryEffects,
			IntegrationsEffects,
			SupplementalSalaryEffects,
			DashboardEffects,
			PunchClockEffects,
			SickDaysEffects,
		]),
		!environment.production ? StoreDevtoolsModule.instrument({
			serialize: {
				replacer: serializeNGRXValues,
			},
		}) : [],
		AngularWebLibModule,
		CommonModule,
		FormsModule,
		TextMaskModule,
		SharedModule,
		ReactiveFormsModule,
	],
	providers: [
		AuthGuard,
		NoAuthGuard,
		AdminGuard,
		UserGuard,
		{
			provide: HTTP_INTERCEPTORS,
			useClass: TokenInterceptor,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: NullToEmptyStringInterceptor,
			multi: true,
		},
		{
			provide: APP_INITIALIZER,
			useFactory: AppInitializer,
			multi: true,
			deps: [AuthService, LocalStorageService, InternationalizationService, Store],
		},
		DateService,
		{
			provide: REDUCER_TOKEN,
			deps: [],
			useFactory: getReducers,
		},
		PayrollService,
		IntegrationService,
		ShiftDraftService,
		DayNoteService,
		ActivityService,
		StatisticService,
		ShiftTemplateService,
	],
	entryComponents: [
		ShiftPopupComponent,
		DayNotePopupComponent,
		SelectShiftDraftPopupComponent,
		SelectShiftTemplatePopupComponent,
		DayNotesPopupComponent,
	],
	declarations: [
		ShiftPopupComponent,
		DayNotePopupComponent,
		SelectShiftDraftPopupComponent,
		SelectShiftTemplatePopupComponent,
		DayNotesPopupComponent,
	],
})
export class CoreModule {
}
